// web/src/App.tsx
import React, { Suspense, useEffect, useRef, useState } from "react";
import {
  Link,
  Navigate,
  Route,
  Routes,
  useParams,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Share2 } from "lucide-react";

import Landing from "@/pages/Landing";
import Splash from "@/pages/Splash";
import Dashboard from "@/pages/Dashboard";
import Settings from "@/pages/Settings";
import SearchPage from "@/pages/Search";
import ProfilePublic from "./pages/ProfilePublic";
import Inbox from "@/pages/Inbox";
import MyCollection from "@/pages/MyCollection";
import FollowersPage from "@/pages/Followers";
import ScoreboardNews from "@/pages/ScoreboardNews";
import Trades from "@/pages/Trades";
import HeatmapPage from "@/pages/HeatmapPage";
import Scribex from "@/pages/Scribex";

// Onboarding
import ClaimUsername from "@/pages/ClaimUsername";
import OnboardingWizard from "@/pages/OnboardingWizard";

// Chrome
import NotificationsBell from "@/components/NotificationsBell";
import AvatarMenu from "@/components/AvatarMenu";
import BottomNav from "@/components/BottomNav";
import Scoreboard from "@/components/Scoreboard";

// Firebase / auth
import { onAuthStateChanged } from "firebase/auth";
import { auth, db, waitForAuth } from "@/lib/firebase";
import { updateUserRank } from "@/data/rank";
import { ToastProvider, Toaster, MascotIcon } from "@/components/ui/use-toast";

// Ack gate + legal
import TermsAck from "@/pages/TermsAck";
import LegalTerms from "@/pages/legal/Terms";
import LegalPrivacy from "@/pages/legal/Privacy";
import LegalCommunity from "@/pages/legal/Community";
import LegalCopyright from "@/pages/legal/Copyright";
import LegalData from "@/pages/legal/Data";
import LegalDMCA from "@/pages/legal/Dmca";

// Firestore (ack gate check)
import { doc, getDoc } from "firebase/firestore";
import { ProfileShareModal } from "@/components/ProfileShareModal";

// Gates / admin
import { useOnboardingGate } from "@/lib/hooks/useOnboardingGate";
import AdminLayout from "@/layouts/AdminLayout";
import AdminLgsPage from "@/pages/admin/lgs";

// New: Overlays / routes
import QuickSearchOverlay from "@/components/QuickSearchOverlay";
import EventsPage from "@/pages/Events";
import EventDetailPage from "@/pages/EventDetail";
import GlobalCardDetailsHost from "@/components/modals/GlobalCardDetailsHost";
import CommunityFeed from "@/pages/CommunityFeed";
import CommunityFeedPublic from "@/pages/CommunityFeedPublic"; // now exports CommunityFeedPublic component
import UnderConstruction from "./components/UnderConstruction";
import { OfferComposerRoot } from "@/components/offers/OfferComposerModal";

import "leaflet/dist/leaflet.css"; // (restored; required for map visuals)

type CSSPropertiesWithVars = React.CSSProperties & Record<`--${string}`, string | number>;

/** Slugs we don't treat as usernames */
const RESERVED_SLUGS = new Set<string>([
  "",
  "app",
  "admin",
  "login",
  "logout",
  "signup",
  "settings",
  "api",
  "about",
  "help",
  "dashboard",
  "search",
  "marketplace",
  "inbox",
  "splash",
  "collection",
  "followers",
  // onboarding/flows
  "termsack",
  "claim-username",
  // group for legal
  "legal",
  "terms",
  "privacy",
  "community",
  "copyright",
  "data",
  "dmca",
  // added
  "events",
  "event",
]);

/** Map reserved slug -> actual route to avoid 404s for legacy/short paths */
function reservedRedirectFor(slug: string): string {
  switch (slug) {
    case "":
      return "/";
    // legal short paths -> /legal/*
    case "terms":
      return "/legal/terms";
    case "privacy":
      return "/legal/privacy";
    case "community":
      return "/legal/community";
    case "copyright":
      return "/legal/copyright";
    case "data":
      return "/legal/data";
    case "dmca":
      return "/legal/dmca";
    // explicit app routes
    case "dashboard":
    case "search":
    case "inbox":
    case "splash":
    case "collection":
    case "followers":
    case "settings":
    case "termsack":
    case "claim-username":
      return `/${slug}`;
    // added: events
    case "events":
    case "event":
      return "/events";
    // legacy marketplace -> community
    default:
      if (slug === "marketplace") return "/community";
      return "/";
  }
}

/** Wrap public pages; if user is logged in, redirect to /dashboard */
function RedirectIfAuthed({ children }: { children: JSX.Element }): JSX.Element {
  const location = useLocation();
  const initialAuthed = Boolean(auth.currentUser);
  const [authed, setAuthed] = useState<boolean>(initialAuthed);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setAuthed(Boolean(u)));
    return () => unsub();
  }, []);

  if (authed) {
    return <Navigate to="/dashboard" replace state={{ from: location }} />;
  }
  return children;
}

/** Guard for routes that require legal/age acknowledgement. */
function RequireAcks({ children }: { children: JSX.Element }): JSX.Element {
  const location = useLocation();
  const [checking, setChecking] = useState<boolean>(true);
  const [ok, setOk] = useState<boolean>(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setChecking(false);
        setOk(false);
        return;
      }
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        type Legal = {
          acceptedAt?: unknown;
          accepted?: boolean;
          ageConfirmed?: boolean;
          age?: number;
        };
        const data = snap.exists() ? (snap.data() as { legal?: Legal }) : {};
        const legal = (data?.legal ?? {}) as Legal;
        const accepted =
          Boolean(legal.acceptedAt) || legal.accepted === true || false;
        const ageOk =
          legal.ageConfirmed === true ||
          (typeof legal.age === "number" && legal.age >= 13);
        setOk(accepted && ageOk);
      } catch {
        setOk(false);
      } finally {
        setChecking(false);
      }
    });
    return () => unsub();
  }, []);

  if (checking) {
    return (
      <main className="mx-auto max-w-6xl px-4 py-8">
        <p className="opacity-80">Checking consent…</p>
      </main>
    );
  }

  if (!ok) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/termsack?next=${next}`} replace />;
  }

  return children;
}

/* ===========================
   Header Mascot + Wizard
=========================== */

function HeaderCourier({
  animKey,
  pathSpanPx,
  paused = false,
  wizardScenario = false,
}: {
  animKey: number;
  pathSpanPx: number;
  paused?: boolean;
  wizardScenario?: boolean;
}): JSX.Element {
  return (
    <>
      <style>{`
        :root { --anim-duration: 14s; }
        .hdr-anim-stage { position: absolute; inset: 0; pointer-events: none; z-index: 0; }
        .bob {
          --path-span: var(--path-span-px, 38vw);
          --stop-frac: 0.6667;
          position: absolute;
          right: 84px;
          bottom: 6px;
          transform: translateX(0);
          animation: var(--bob-anim, bob-walk) var(--anim-duration) steps(40, end) 1 both;
          opacity: 0;
          image-rendering: pixelated;
          filter: drop-shadow(0 1px 0 rgba(0,0,0,.85)) drop-shadow(0 0 6px rgba(0,0,0,.4));
        }
        @keyframes bob-walk {
          0% { opacity: 0; transform: translateX(0) translateY(0); }
          6% { opacity: 1; }
          46% { transform: translateX(calc(-1 * var(--path-span))) translateY(0); }
          50% { transform: translateX(calc(-1 * var(--path-span))) translateY(-1px); }
          56% { transform: translateX(calc(-1 * var(--path-span))) translateY(0); }
          96% { opacity: 1; transform: translateX(0) translateY(0); }
          100% { opacity: 0; }
        }
        @keyframes bob-wizard {
          0% { opacity: 0; transform: translateX(0) translateY(0); }
          6% { opacity: 1; }
          46% { transform: translateX(calc(-1 * var(--path-span))) translateY(0); }
          50% { transform: translateX(calc(-1 * var(--path-span))) translateY(-1px); }
          56% { transform: translateX(calc(-1 * var(--path-span))) translateY(0); }
          68% { transform: translateX(calc(-1 * var(--path-span) * var(--stop-frac))); }
          74% { transform: translateX(calc(-1 * var(--path-span) * var(--stop-frac))); }
          90% { transform: translateX(calc(-1 * var(--path-span) * 0.15)); }
          96% { opacity: 1; transform: translateX(0) translateY(0); }
          100% { opacity: 0; }
        }
        .bob .cap { position: absolute; top: -3px; left: 5px; width: 12px; height: 6px; background:#ef4444; border:1px solid #111827; border-radius:1px; image-rendering:pixelated; transform:rotate(-6deg); box-shadow:1px 0 0 rgba(0,0,0,.5); }
        .bob .carry { position:absolute; top:2px; left:12px; width:18px; height:14px; opacity:0; image-rendering:pixelated; animation: var(--carry-anim, carry-visible) var(--anim-duration) linear 1 both, carry-bob 900ms ease-in-out infinite; }
        .bob .carry .card { position:absolute; width:7px; height:9px; border:1px solid #111827; border-radius:1px; box-shadow:0 0 0 1px rgba(0,0,0,.15) inset; transform-origin:center; }
        .bob .carry .card:nth-child(1){ left:0; top:3px; background:#fca5a5; transform:rotate(-6deg); }
        .bob .carry .card:nth-child(2){ left:6px; top=1px; background:#fde68a; transform:rotate(4deg); }
        .bob .carry .card:nth-child(3){ left=4px; top=-1px; background:#86efac; transform:rotate(-2deg); }
        @keyframes carry-visible { 0%,54% { opacity:0; } 56%,95% { opacity:1; } 100% { opacity:0; }
        }
        @keyframes carry-visible-wiz { 0%,62% { opacity:0; } 64%,66% { opacity:1; } 68%,100% { opacity:0; } }
        @keyframes carry-bob { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-1px); } }
        .bob .carry-bones { position:absolute; top:3px; left:12px; width:18px; height:14px; opacity:0; image-rendering:pixelated; animation: bones-carry var(--anim-duration) linear 1 both; }
        .bob .carry-bones .skull { position:absolute; bottom:2px; left:2px; width:10px; height:8px; background:#e5e7eb; border:2px solid #111827; border-radius:2px; }
        .bob .carry-bones .bone1, .bob .carry-bones .bone2 { position:absolute; bottom:0; left:8px; width:12px; height:2px; background:#e5e7eb; border:1px solid #111827; border-radius:1px; }
        .bob .carry-bones .bone2 { bottom:1px; left:10px; }
        @keyframes bones-carry { 0%,86% { opacity:0; transform:translateY(0); } 88%,98% { opacity:1; transform:translateY(-1px); } 100% { opacity:0; } }
        @media (prefers-reduced-motion: reduce) { .bob, .bob .carry, .bob .carry-bones { animation:none !important; opacity:0; } }
      `}</style>
      <div className="hdr-anim-stage" aria-hidden="true">
        <div
          key={animKey}
          className="bob"
          style={
            {
              "--path-span-px": `${Math.max(24, pathSpanPx)}px`,
              "--bob-anim": (wizardScenario ? "bob-wizard" : "bob-walk") as any,
              "--carry-anim": (wizardScenario ? "carry-visible-wiz" : "carry-visible") as any,
              animationPlayState: paused ? "paused" : "running",
            } as CSSPropertiesWithVars
          }
        >
          <MascotIcon variant="info" bounce={false} className="h-7 w-7" />
          <div className="cap" />
          <div className="carry">
            <div className="card" />
            <div className="card" />
            <div className="card" />
          </div>
          {wizardScenario && (
            <div className="carry-bones">
              <div className="skull" />
              <div className="bone1" />
              <div className="bone2" />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function CardBurst({ animKey }: { animKey: number }): JSX.Element {
  return (
    <>
      <style>{`
        .burst { position: absolute; left: calc(100% - 2px); bottom: 0; width: 160px; height: 48px; pointer-events: none; overflow: visible; }
        .burst .stack { position: absolute; bottom: 4px; left: 0; opacity: 0; transform: translateX(0) translateY(0) scale(1); animation: burst-seq var(--anim-duration) linear 1 both; image-rendering: pixelated; filter: drop-shadow(0 0 0 #111827); }
        .burst .card { position: absolute; width: 10px; height: 13px; border: 2px solid #111827; border-radius: 1px; background: #fca5a5; }
        .burst .card:nth-child(2) { left:12px; top:-2px; background:#fde68a; }
        .burst .card:nth-child(3) { left:5px; top:-5px; background:#86efac; }
        .burst .card:nth-child(4) { left:18px; top:-6px; background:#93c5fd; }
        @keyframes burst-seq {
          0%,34% { opacity:0; transform:translateX(0) translateY(0) scale(.6); }
          35% { opacity:1; transform:translateX(4px) translateY(-10px) scale(1.6); }
          40% { opacity:1; transform:translateX(10px) translateY(2px) scale(1.2); }
          48% { opacity:1; transform:translateX(18px) translateY(8px) scale(1); }
          60% { opacity:1; transform:translateX(18px) translateY(8px) scale(1); }
          70% { opacity:0; transform:translateX(18px) translateY(8px) scale(1); }
          100% { opacity:0; }
        }
        @media (prefers-reduced-motion: reduce) { .burst .stack { animation:none !important; opacity:0; } }
      `}</style>
      <div className="burst" aria-hidden="true">
        <div key={animKey} className="stack">
          <div className="card" />
          <div className="card" />
          <div className="card" />
          <div className="card" />
        </div>
      </div>
    </>
  );
}

function HeaderWizardInterlude({
  animKey,
  active,
  stopRightPx,
}: {
  animKey: number;
  active: boolean;
  stopRightPx?: number;
}): JSX.Element | null {
  if (!active) return null;
  return (
    <>
      <style>{`
        :root { --wizard-duration: 14000ms; }
        .wiz-stage { position:absolute; inset:0; pointer-events:none; z-index:50; }
        .wizard { position:absolute; top:8px; right:var(--wiz-right, 220px); width:26px; height:34px; opacity:0; transform:translate(0,0) scale(1); image-rendering:pixelated; filter:drop-shadow(0 1px 0 rgba(0,0,0,.85)) drop-shadow(0 0 6px rgba(0,0,0,.4)); animation:wiz-seq var(--wizard-duration) linear 1 both; }
        .wizard .robe { position:absolute; bottom:0; left:3px; width:20px; height:22px; background:#1e3a8a; border:2px solid #0b1220; border-radius:3px 3px 4px 4px; box-shadow: inset 0 -2px 0 #0f215c, inset 0 2px 0 #2d4bb2; }
        .wizard .trim { position:absolute; bottom:3px; left:5px; width:16px; height:4px; background:#e5e7eb; border:1px solid #0b1220; border-radius:1px; opacity:.95; }
        .wizard .head { position:absolute; top:0; left:7px; width:12px; height:10px; background:#d1d5db; border:2px solid #0b1220; border-radius:2px; }
        .wizard .hair { position:absolute; top:-1px; left:5px; width:16px; height:6px; background:#e5e7eb; border:2px solid #0b1220; border-radius:2px; }
        .wizard .beard { position:absolute; top:8px; left:6px; width:14px; height:8px; background:#f3f4f6; border:2px solid #0b1220; border-radius:0 0 3px 3px; }
        .wizard .hat { position:absolute; top:-6px; left:6px; width:14px; height:8px; background:#1e40af; border:2px solid #0b1220; border-radius:2px 2px 4px 4px; transform:rotate(-6deg); box-shadow:0 1px 0 #3349c8 inset; }
        .wizard .staff { position:absolute; bottom:0; right:-2px; width:4px; height:28px; background:#8b5a2b; border:2px solid #0b1220; border-radius:2px; box-shadow: inset 0 -1px 0 #5e3b1b; }

        .drop-cards { position:absolute; bottom:8px; right:var(--wiz-right, 220px); width:26px; height:16px; opacity:0; animation:dropcards-seq var(--wizard-duration) linear 1 both; }
        .drop-cards .card { position:absolute; width:7px; height:9px; border:1px solid #111827; border-radius:1px; box-shadow:0 0 0 1px rgba(0,0,0,.15) inset; background:#fde68a; }
        .drop-cards .card:nth-child(1){ left:0; top:5px; background:#fca5a5; transform:rotate(-12deg); }
        .drop-cards .card:nth-child(2){ left:8px; top=2px; background:#fde68a; transform:rotate(6deg); }
        .drop-cards .card:nth-child(3){ left:14px; top=6px; background:#86efac; transform:rotate(-4deg); }

        .bolt-ray { position:absolute; top:22px; right:calc(var(--wiz-right, 220px) - 2px); height:2px; width:160px; background:linear-gradient(90deg, rgba(250,204,21,1) 0%, rgba(253,230,138,.9) 40%, rgba(254,243,199,0) 100%); box-shadow:0 0 6px rgba(250,204,21,.9), 0 0 12px rgba(253,230,138,.6); transform-origin:right center; transform:rotate(8deg) scaleX(0); opacity:0; animation:bolt-ray-seq var(--wizard-duration) linear 1 both; }

        .fireball { position:absolute; top:0px; right:calc(var(--wiz-right, 220px) + 120px); width:16px; height:16px; border-radius:50%; background:radial-gradient(circle at 35% 35%, #fff, #fde047 38%, #fb923c 62%, #dc2626 90%); box-shadow:0 0 10px 4px rgba(251,146,60,.55), 0 0 20px 10px rgba(220,38,38,.35); opacity:0; transform:translate(0,-50px) scale(.8); animation:fireball-arc var(--wizard-duration) linear 1 both; }

        .bones { position:absolute; bottom:6px; right:var(--wiz-right, 220px); width:22px; height:12px; opacity:0; transform:translateY(-10px); animation:bones-seq var(--wizard-duration) linear 1 both; }
        .bones .skull { position:absolute; bottom:2px; left:2px; width:10px; height:8px; background:#e5e7eb; border:2px solid #111827; border-radius:2px; }
        .bones .skull::before,.bones .skull::after { content:""; position:absolute; top:2px; width:2px; height:2px; background:#111827; border-radius:50%; }
        .bones .skull::before{ left:2px; } .bones .skull::after{ right:2px; }
        .bones .stack1,.bones .stack2 { position:absolute; bottom:0; width:14px; height:2px; background:#e5e7eb; border:1px solid #111827; border-radius:1px; }
        .bones .stack2{ left:4px; bottom:1px; }

        @keyframes wiz-seq { 0%,66% { opacity:0; transform:translate(0,0) scale(1); } 68% { opacity:1; } 72% { opacity:1; } 78% { opacity:0; } 100% { opacity:0; } }
        @keyframes dropcards-seq { 0%,64% { opacity:0; } 66%,70% { opacity:1; } 72%,100% { opacity:0; } }
        @keyframes bolt-ray-seq { 0%,68% { opacity:0; transform:rotate(8deg) scaleX(0); } 70% { opacity:1; transform:rotate(8deg) scaleX(1.0); } 72% { opacity:1; transform:rotate(6deg) scaleX(.8); } 74% { opacity:0; transform:rotate(8deg) scaleX(0.2); } 100% { opacity:0; } }
        @keyframes fireball-arc { 0%,72% { opacity:0; transform:translate(0,-50px) scale(.8); } 74% { opacity:1; transform:translate(-100px,20px) scale(1.1); } 76% { opacity:1; transform:translate(-140px,30px) scale(1.1); } 78% { opacity:0; transform:translate(-140px,30px) scale(.9); } 100% { opacity:0; } }
        @keyframes bones-seq { 0%,78% { opacity:0; transform:translateY(-10px); } 82% { opacity:1; transform:translateY(0); } 92% { opacity:1; transform:translateY(0); } 96%,100% { opacity:0; transform:translateY(0); } }
        @media (prefers-reduced-motion: reduce) { .wizard, .drop-cards, .bolt-ray, .fireball, .bones { animation:none !important; opacity:0; } }
      `}</style>
      <div
        className="wiz-stage"
        aria-hidden="true"
        style={{ ["--wiz-right" as any]: `${stopRightPx ?? 220}px` }}
      >
        <div key={animKey} className="wizard">
          <div className="hat" />
          <div className="hair" />
          <div className="head" />
          <div className="beard" />
          <div className="robe" />
          <div className="trim" />
          <div className="staff" />
        </div>
        <div className="bolt-ray" />
        <div className="fireball" />
        <div className="bones">
          <div className="skull" />
          <div className="stack1" />
          <div className="stack2" />
        </div>
        <div className="drop-cards">
          <div className="card" />
          <div className="card" />
          <div className="card" />
        </div>
      </div>
    </>
  );
}

/* ===========================
            App
=========================== */

export default function App(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const isLanding = location.pathname === "/";
  const isScribex = location.pathname.toLowerCase() === "/scribex";
  const isInbox = location.pathname.startsWith("/inbox");

  // fonts-ready class
  useEffect(() => {
    document.documentElement.classList.add("fonts-ready");
  }, []);

  // keep user rank fresh on auth changes
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) return;
      try {
        await updateUserRank(user.uid);
      } catch (e) {
        console.warn("updateUserRank failed:", e);
      }
    });
    return () => unsub();
  }, []);

  // Shared animation key
  const [animKey, setAnimKey] = useState(0);

  // Wizard scenario every 4th cycle
  const wizardEnabled = animKey % 4 === 3;
  const [wizardActive, setWizardActive] = useState(false);

  useEffect(() => {
    let t1: number | undefined;
    let t2: number | undefined;
    if (wizardEnabled) {
      t1 = window.setTimeout(() => setWizardActive(true), 0);
      t2 = window.setTimeout(() => setWizardActive(false), 14000);
    } else {
      setWizardActive(false);
    }
    return () => {
      if (t1) clearTimeout(t1);
      if (t2) clearTimeout(t2);
    };
  }, [wizardEnabled, animKey]);

  // Loop animation with jitter
  useEffect(() => {
    let t = 0 as unknown as number;
    const schedule = () => {
      const jitter = Math.floor(24000 + Math.random() * 22000);
      t = window.setTimeout(() => {
        setAnimKey((k) => k + 1);
        schedule();
      }, jitter);
    };
    schedule();
    return () => clearTimeout(t);
  }, []);

  // Path span measurement
  const headerRef = useRef<HTMLDivElement | null>(null);
  const brandWrapRef = useRef<HTMLDivElement | null>(null);
  const [pathSpanPx, setPathSpanPx] = useState<number>(240);
  useEffect(() => {
    function compute() {
      const header = headerRef.current;
      const brand = brandWrapRef.current;
      if (!header || !brand) return;
      const h = header.getBoundingClientRect();
      const b = brand.getBoundingClientRect();
      const startX = h.width - 84;
      const targetX = b.right - h.left + 6;
      const span = Math.max(24, Math.floor(startX - targetX));
      setPathSpanPx(span);
    }
    compute();
    const onResize = () => compute();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // One-time trigger from onboarding redirect
  const [onboardingTrigger, setOnboardingTrigger] = useState(false);
  const seenRef = useRef(false);
  useEffect(() => {
    const st = (location.state as any) || {};
    const should = Boolean(st?.startTour || st?.justOnboarded);
    if (should && !seenRef.current) {
      seenRef.current = true;
      setOnboardingTrigger(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.key, navigate, location.pathname]);

  // Onboarding gate (no-op if satisfied)
  useOnboardingGate();

  // Compute wizard stop position aligned with Bob’s pause point
  const stopRightPx = 84 + Math.round(pathSpanPx * (1 - 1 / 3));

  useEffect(() => {
    let mounted = true;
    waitForAuth().then((u) => {
      console.info('[auth] ready', u?.uid ?? null);
      if (!mounted) return;
      // now it's safe to start listeners/queries that require request.auth
      // e.g. startInboxListeners();
    });
    return () => { mounted = false; };
  }, []);

  const [showProfileShare, setShowProfileShare] = useState(false);
  const [username, setUsername] = useState<string | null>(null);

  // Load canonical username from Firestore (never use displayName / uid directly)
  useEffect(() => {
    let cancel = false;
    (async () => {
      const u = auth.currentUser;
      if (!u) {
        if (!cancel) setUsername(null);
        return;
      }
      try {
        const snap = await getDoc(doc(db, "users", u.uid));
        if (cancel) return;
        const data = snap.exists() ? snap.data() : null;
        const uname = data?.username || null;
        setUsername(uname);
      } catch {
        if (!cancel) setUsername(null);
      }
    })();
    return () => { cancel = true; };
  }, [auth.currentUser]);

  return (
    <>
      <ToastProvider>
        <div className={isScribex ? "min-h-screen" : "min-h-screen pb-[72px] sm:pb-[80px]"}>
          {/* Global watermark (behind all content) */}
          {!isLanding && !isScribex && (
            <>
              <style>{`
                #app-watermark {
                  position: fixed;
                  inset: 0;
                  z-index: 0;
                  pointer-events: none;
                }
                #app-watermark::after {
                  content: "";
                  position: absolute;
                  top: 50%;
                  left: 50%;
                  transform: translate(-50%, -50%);
                  width: min(70vw, 760px);
                  aspect-ratio: 771 / 722;
                  background: url("/images/watermark-spellgather.png") center / contain no-repeat;
                  filter: brightness(0.5);
                  opacity: 0.22;
                }
                @media (max-width: 640px) {
                  #app-watermark::after {
                    width: min(78vw, 520px);
                    opacity: 0.2;
                  }
                }
              `}</style>
              <div id="app-watermark" aria-hidden="true" />
            </>
          )}

          {/* Top App Header (hidden on Landing and SCRIBEX page) */}
          {!isLanding && !isScribex && (
            <header
              data-app-chrome="top"
              id="app-topbar"
              className="sticky top-0 z-50 bg-black/40 backdrop-blur border-b border-gray-800"
              data-tour="bob-mascot"
            >
              <div
                ref={headerRef}
                className="mx-auto max-w-6xl px-4 py-3 flex items-center justify-between relative"
              >
                {/* Brand: logo + wordmark */}
                <div ref={brandWrapRef} className="relative z-20">
                  <Link to="/" className="flex items-center gap-2 group" aria-label="Go to home">
                    <img
                      src="/images/spellgather-logo.png"
                      alt="Spellgather logo"
                      className="h-9 w-9 sm:h-16 sm:w-16 rounded-[6px] ring-1 ring-black/20 shadow"
                      loading="eager"
                      decoding="async"
                    />
                    <span className="text-2xl font-extrabold text-white group-hover:opacity-90 transition-opacity">
                      Spellgather
                    </span>
                  </Link>
                  {/* Pixel card burst appears behind the wordmark */}
                  <CardBurst animKey={animKey} />
                </div>

                {/* Right side icons */}
                <div className="flex items-center gap-2 relative z-10">
                  <button
                    type="button"
                    onClick={() => setShowProfileShare(true)}
                    className="p-2 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 hover:text-white transition"
                    aria-label="Share my profile"
                  >
                    <Share2 className="w-5 h-5" />
                  </button>
                  <NotificationsBell />
                  <AvatarMenu />
                </div>
              </div>
            </header>
          )}

          {/* Main container: full-bleed for Inbox, constrained for others */}
          <main className={isScribex ? "" : isInbox ? "px-2" : "mx-auto max-w-6xl px-4"}>
            <Suspense fallback={<div className="p-6 text-sm text-white/60">Loading…</div>}>
              <RoutesErrorBoundary>
                <LocationLogger />
                <Routes>
                  {/* Public / landing */}
                  <Route path="/splash" element={<Splash />} />
                  {/* SCRIBEX page */}
                  <Route path="/SCRIBEX" element={<Scribex />} />
                  <Route path="/scribex" element={<Scribex />} />
                  <Route
                    path="/"
                    element={
                      <RedirectIfAuthed>
                        <Landing />
                      </RedirectIfAuthed>
                    }
                  />

                  {/* Onboarding */}
                  <Route path="/claim-username" element={<ClaimUsername />} />
                  <Route path="/termsack" element={<TermsAck />} />
                  <Route path="/onboarding" element={<OnboardingWizard />} />

                  {/* App pages */}
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/collection" element={<MyCollection />} />

                  {/* Community feed (authenticated) & public read-only */}
                  <Route path="/community" element={<CommunityFeed />} />
                  <Route path="/community/public" element={<CommunityFeedPublic />} />

                  {/* Legacy redirect for old marketplace links */}
                  <Route path="/marketplace" element={<Navigate to="/community" replace />} />

                  <Route path="/search/*" element={<SearchPage />} />
                  <Route path="/inbox" element={<Inbox />} />
                  <Route path="/followers" element={<FollowersPage />} />
                  <Route path="/scoreboard/news" element={<ScoreboardNews />} />
                  <Route path="/trades" element={<Trades />} />
                  <Route path="/heatmap" element={<HeatmapPage />} />

                  {/* Events */}
                  <Route path="/events" element={<EventsPage />} />
                  <Route path="/event/:eid" element={<EventDetailPage />} />
                  <Route path="/public/events" element={<EventsPage readOnly />} />

                  {/* Settings (acks) */}
                  <Route
                    path="/settings"
                    element={
                      <RequireAcks>
                        <Settings />
                      </RequireAcks>
                    }
                  />

                  {/* Legacy deep link support */}
                  <Route path="/profile/settings" element={<Navigate to="/settings" replace />} />

                  {/* Legal */}
                  <Route path="/legal/terms" element={<LegalTerms />} />
                  <Route path="/legal/privacy" element={<LegalPrivacy />} />
                  <Route path="/legal/community" element={<LegalCommunity />} />
                  <Route path="/legal/copyright" element={<LegalCopyright />} />
                  <Route path="/legal/data" element={<LegalData />} />
                  <Route path="/legal/dmca" element={<LegalDMCA />} />

                  {/* Admin */}
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route path="lgs" element={<AdminLgsPage />} />
                  </Route>

                  {/* ORDER MATTERS: put the new pretty deck route BEFORE other wide /:slug routes */}
                  <Route path="/:slug/:deckSlug" element={<ProfilePublic />} />
                  <Route path="/:slug" element={<ProfilePublic />} />
                  <Route path="/@:slug/:deckSlug" element={<ProfilePublic />} />
                  <Route path="/@:slug" element={<ProfilePublic />} />

                  {/* (Optional) Legacy short alias redirect support */}
                  <Route path="/u/:username" element={<LegacyUserRedirect />} />

                  {/*
                    OPTIONAL STRICT RESERVED-SLUG GUARD:
                    If you want to reinstate reserved slug protection, replace the plain /:slug route above with:
                    <Route path="/:slug" element={<UsernameRouter />} />
                    and ensure UsernameRouter uses RESERVED_SLUGS.
                  */}

                  {/* Catch-all fallback */}
                  <Route path="*" element={<UnderConstruction />} />
                </Routes>
              </RoutesErrorBoundary>
            </Suspense>
          </main>

          {/* Bottom navigation (fixed) — hidden on Landing and SCRIBEX page */}
          {!isLanding && !isScribex && (
            <footer
              data-app-chrome="bottom"
              id="bottom-navbar"
              className="fixed inset-x-0 bottom-0 z-40 h-16 bg-black/60 backdrop-blur border-t border-gray-800 pb-[env(safe-area-inset-bottom)]"
            >
              <div className="mx-auto max-w-6xl h-full px-2 overflow-x-auto whitespace-nowrap">
                <BottomNav />
              </div>
            </footer>
          )}

          {/* Global overlays */}
          <QuickSearchOverlay />
          <GlobalCardDetailsHost />
          <OfferComposerRoot />
          <Toaster position="bottom-right" />
        </div>
      </ToastProvider>
      

      {/* NEW: Profile Share Modal mount */}
      <ProfileShareModal
        open={showProfileShare}
        username={username} // only canonical username
        onClose={() => setShowProfileShare(false)}
      />
    </>
  );
}

/** Defers reserved slugs and passes everything else to public profile */
function UsernameRouter(): JSX.Element {
  const { username } = useParams<{ username?: string }>();
  const slug = (username ?? "").toLowerCase();

  if (RESERVED_SLUGS.has(slug)) {
    const target = reservedRedirectFor(slug);
    return <Navigate to={target} replace />;
  }

  return <ProfilePublic />;
}

function LegacyUserRedirect(): JSX.Element {
  const { username } = useParams<{ username?: string }>();
  if (!username) return <Navigate to="/" replace />;
  return <Navigate to={`/${encodeURIComponent(username)}`} replace />;
}

/** Error boundary for routes; shows error message instead of crashing the whole app */
function RoutesErrorBoundary({ children }: { children: React.ReactNode }) {
  const [err, setErr] = React.useState<Error | null>(null);
  if (err) {
    return (
      <div className="p-6 text-red-300 text-sm font-mono space-y-4">
        <h2 className="text-lg font-semibold text-red-400">Route Error</h2>
        <pre className="whitespace-pre-wrap">
          {err.message + "\n" + (err.stack || "")}
        </pre>
        <button
          onClick={() => location.reload()}
          className="px-3 py-1 rounded bg-white/10 hover:bg-white/20"
        >
          Reload
        </button>
      </div>
    );
  }
  return <ErrorTrap onError={setErr}>{children}</ErrorTrap>;
}

function ErrorTrap({
  onError,
  children,
}: {
  onError: (e: Error) => void;
  children: React.ReactNode;
}) {
  try {
    return <>{children}</>;
  } catch (e: any) {
    onError(e);
    return null;
  }
}

function LocationLogger() {
  const loc = useLocation();
  React.useEffect(() => {
    console.log("[Route] location", loc.pathname + loc.search);
  }, [loc]);
  return null;
}
